import { useRoute, Link, useLocation } from "wouter";
import { Layout } from "@/components/layout";
import {
  useGetSnippet,
  forkSnippet,
  getListMySnippetsQueryKey,
  getGetSnippetQueryKey,
} from "@workspace/api-client-react";
import { useUser } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CodeSandbox } from "@/components/sandbox/code-sandbox";
import { ArrowLeft, GitFork, LogIn } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { showRateLimitToast } from "@/lib/apiErrors";

const PENDING_FORK_KEY = "mainloop:pending-fork";

export default function SnippetShare() {
  const [, params] = useRoute("/s/:id");
  const id = params?.id ?? "";
  const { data: snippet, isLoading, isError } = useGetSnippet(id, {
    query: { enabled: !!id, queryKey: getGetSnippetQueryKey(id) },
  });
  const { isSignedIn, isLoaded } = useUser();
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [forking, setForking] = useState(false);
  const autoForkAttempted = useRef(false);

  const performFork = async () => {
    if (!id) return;
    setForking(true);
    try {
      const forked = await forkSnippet(id);
      qc.invalidateQueries({ queryKey: getListMySnippetsQueryKey() });
      toast({
        title: "Forked",
        description: "A copy lives in your snippets now.",
      });
      if (forked.lessonId) {
        setLocation(`/lessons/${forked.lessonId}`);
      } else {
        setLocation("/my-classes");
      }
    } catch (e) {
      if (!showRateLimitToast(e)) {
        toast({
          title: "Couldn't fork",
          description: e instanceof Error ? e.message : "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setForking(false);
    }
  };

  // Resume the fork after a sign-in round-trip. We stash the snippet id in
  // sessionStorage before redirecting to /sign-in; on return, if the user is
  // signed in and the stash matches this snippet, auto-fork once.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !id || autoForkAttempted.current) return;
    try {
      const pending = sessionStorage.getItem(PENDING_FORK_KEY);
      if (pending === id) {
        autoForkAttempted.current = true;
        sessionStorage.removeItem(PENDING_FORK_KEY);
        void performFork();
      }
    } catch {
      // sessionStorage may be unavailable; safe to ignore.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, id]);

  if (isLoading)
    return (
      <Layout>
        <div className="container mx-auto px-4 py-24 text-center" role="status">
          Loading snippet…
        </div>
      </Layout>
    );
  if (isError || !snippet)
    return (
      <Layout>
        <div className="container mx-auto px-4 py-24 text-center">
          Snippet not found.
        </div>
      </Layout>
    );

  const handleFork = async () => {
    if (!isSignedIn) {
      try {
        sessionStorage.setItem(PENDING_FORK_KEY, id);
      } catch {
        // ignore
      }
      setLocation(`/sign-in?redirect_url=${encodeURIComponent(`/s/${id}`)}`);
      return;
    }
    await performFork();
  };

  return (
    <Layout>
      <div className="border-b border-border relative overflow-hidden">
        <div
          className="absolute inset-0 bg-grid pointer-events-none opacity-50"
          aria-hidden="true"
        />
        <div className="container mx-auto px-4 py-12 relative z-10 max-w-5xl">
          {snippet.lesson ? (
            <Link
              href={`/lessons/${snippet.lesson.id}`}
              className="inline-flex items-center font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-2" /> From ·{" "}
              {snippet.lesson.courseTitle} · Lesson{" "}
              {String(snippet.lesson.orderIndex).padStart(2, "0")}
            </Link>
          ) : (
            <Link
              href="/courses"
              className="inline-flex items-center font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-2" /> Browse courses
            </Link>
          )}
          <div className="section-tag mb-3">
            [ shared snippet ] · {snippet.language}
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight mb-4 text-balance">
            {snippet.title}
          </h1>
          <p className="text-muted-foreground">
            Read-only view. Run it here, or fork it into your own account to edit.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 max-w-5xl space-y-6">
        <div className="flex justify-end">
          <Button onClick={handleFork} disabled={forking} className="h-11">
            {isSignedIn ? (
              <>
                <GitFork className="w-4 h-4 mr-2" />
                {forking ? "Forking…" : "Fork into my account"}
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4 mr-2" /> Sign in to fork
              </>
            )}
          </Button>
        </div>
        <CodeSandbox
          language={snippet.language}
          starter={snippet.code}
          lessonId={snippet.lessonId ?? undefined}
          lessonTitle={snippet.title}
          initialSnippet={snippet}
          readOnly
        />
      </div>
    </Layout>
  );
}
